import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { CollapsibleSection } from './CollapsibleSection';
import { AddMailDialog } from './AddMailDialog';
import {
  Loader2, MessagesSquare, Flame, Sparkles, X, Settings2, MailPlus, Send, RefreshCw, CalendarPlus, Video,
} from 'lucide-react';
import { Switch } from './ui/switch';

const SOURCE_STYLES = {
  campaign_reply: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  crm_inbound: 'border-sky-500/40 bg-sky-500/10 text-sky-300',
  manual: 'border-violet-500/40 bg-violet-500/10 text-violet-300',
  checkin: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
};

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '—';

export function BrokerDiscussions() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [draft, setDraft] = useState({ subject: '', body: '' });
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [weeks, setWeeks] = useState('4');
  const [weeksSaving, setWeeksSaving] = useState(false);
  const [autosend, setAutosend] = useState(false);
  const [minDays, setMinDays] = useState('30');
  const [maxDays, setMaxDays] = useState('45');
  const [checkinPreview, setCheckinPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [expandedDone, setExpandedDone] = useState(null);
  const [invite, setInvite] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  const [forceInvite, setForceInvite] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [tone, setTone] = useState({
    salutation: localStorage.getItem('disc.salutation') || 'sehr_geehrt',
    form: localStorage.getItem('disc.form') || 'sie',
  });

  const load = useCallback(() => {
    api.getBrokerDiscussions()
      .then(r => setData(r.data))
      .catch(() => setData(d => d || { discussions: [], settings: { checkin_weeks: 4 }, done_30d: 0, hot_brokers: 0, load_error: true }));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const fetchDraft = async (d, force = false, t = null) => {
    setDrafting(true);
    try {
      const r = await api.draftBrokerDiscussion(d.id, force, t || tone);
      setDraft({ subject: r.data.draft.subject || '', body: r.data.draft.body || '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'KI-Entwurf fehlgeschlagen — du kannst manuell schreiben');
    }
    setDrafting(false);
  };

  const setToneOpt = (key, value) => {
    if (tone[key] === value || drafting || sending) return;
    const next = { ...tone, [key]: value };
    setTone(next);
    localStorage.setItem(`disc.${key === 'salutation' ? 'salutation' : 'form'}`, value);
    if (replyFor) fetchDraft(replyFor, true, next);
  };

  const openReply = (d) => {
    setDraft({ subject: '', body: '' });
    setForceInvite(false);
    setReplyFor(d);
    fetchDraft(d);
  };

  const sendReply = async () => {
    if (!draft.body.trim()) { toast.error('Antworttext fehlt'); return; }
    setSending(true);
    try {
      await api.sendBrokerDiscussion(replyFor.id, { body: draft.body, subject: draft.subject });
      toast.success(`Antwort an ${replyFor.contact_name} gesendet`);
      const sent = replyFor;
      const sentBody = draft.body;
      const force = forceInvite;
      setReplyFor(null);
      load();
      scanForInvite(sent, sentBody, force);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versand fehlgeschlagen');
    }
    setSending(false);
  };

  const scanForInvite = async (d, text, force = false) => {
    try {
      const r = await api.scanBrokerInvite(d.id, text, force);
      if (r.data.suggest) setInvite({ did: d.id, contact_name: d.contact_name, ...r.data });
    } catch {
      if (force) {
        // toggle was ON — open the popup with minimal prefill even if the scan call failed
        setInvite({
          did: d.id, contact_name: d.contact_name, subject: `OLTA / ${d.contact_name || ''}`.trim(),
          description: '', date: '', time: '', duration_min: 30, teams: true,
          attendee_email: d.broker_email || '',
        });
      }
    }
  };

  const openManualInvite = async (d) => {
    setBusy(`invite-${d.id}`);
    try {
      const r = await api.manualBrokerInvite(d.id);
      setInvite({ did: d.id, contact_name: d.contact_name, manual: true, ...r.data });
    } catch {
      setInvite({
        did: d.id, contact_name: d.contact_name, manual: true,
        subject: `OLTA / ${d.contact_name || ''}`.trim(), description: '', date: '', time: '',
        duration_min: 30, teams: true, attendee_email: d.broker_email || '',
      });
    }
    setBusy(null);
  };

  const sendInvite = async () => {
    setInviteSending(true);
    try {
      await api.sendBrokerInvite(invite.did, {
        subject: invite.subject, description: invite.description, date: invite.date,
        time: invite.time, duration_min: parseInt(invite.duration_min, 10) || 30,
        teams: invite.teams, attendee_email: invite.attendee_email,
      });
      toast.success(invite.teams ? 'Teams-Einladung gesendet' : 'Kalender-Einladung gesendet');
      setInvite(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Einladung fehlgeschlagen');
    }
    setInviteSending(false);
  };

  const toggleHot = async (d) => {
    if (!d.contact_id) { toast.error('Kein CRM-Kontakt verknüpft — Broker zuerst als Kontakt anlegen'); return; }
    setBusy(`hot-${d.id}`);
    try {
      await api.setBrokerHeat(d.contact_id, !d.hot);
      toast.success(!d.hot ? '🔥 Als heißer Broker markiert — Check-in-Erinnerungen aktiv' : 'Hot-Markierung entfernt');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    setBusy(null);
  };

  const dismiss = async (d) => {
    setBusy(`dismiss-${d.id}`);
    try {
      await api.dismissBrokerDiscussion(d.id);
      toast.success('Als erledigt markiert');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Aktion fehlgeschlagen'); }
    setBusy(null);
  };

  const saveWeeks = async () => {
    setWeeksSaving(true);
    try {
      const r = await api.saveDiscussionSettings({
        checkin_weeks: parseInt(weeks, 10) || 4,
        checkin_autosend: autosend,
        checkin_min_days: parseInt(minDays, 10) || 30,
        checkin_max_days: parseInt(maxDays, 10) || 45,
      });
      toast.success(r.data.checkin_autosend
        ? `Auto-Versand aktiv: Check-ins alle ${r.data.checkin_min_days}–${r.data.checkin_max_days} Tage (zufällig je Broker)`
        : `Check-in-Rhythmus: alle ${r.data.checkin_weeks} Wochen (manueller Versand)`);
      setSettingsOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setWeeksSaving(false);
  };

  const loadCheckinPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await api.previewCheckin();
      setCheckinPreview(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Vorschau fehlgeschlagen'); }
    setPreviewLoading(false);
  };

  const items = data?.discussions || [];

  return (
    <>
      <CollapsibleSection storageKey="campaigns.discussions" icon={MessagesSquare} iconClass="text-emerald-400"
        title="Broker Discussions" testId="broker-discussions-section"
        right={(
          <span className="flex items-center gap-1.5">
            {(data?.hot_brokers || 0) > 0 && (
              <span className="text-[10px] font-mono border border-orange-500/40 bg-orange-500/10 text-orange-300 rounded px-1.5 py-0.5 flex items-center gap-1"
                data-testid="discussions-hot-count">
                <Flame className="w-3 h-3" /> {data.hot_brokers} hot
              </span>
            )}
            {items.length > 0 && (
              <span className="text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5"
                data-testid="discussions-open-count">{items.length} offen</span>
            )}
            <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="discussions-addmail-btn"
              className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[10px] h-7 px-2">
              <MailPlus className="w-3 h-3 mr-1" /> E-Mail flaggen
            </Button>
            <Button variant="outline" size="sm"
              onClick={() => {
                const s = data?.settings || {};
                setWeeks(String(s.checkin_weeks || 4));
                setAutosend(!!s.checkin_autosend);
                setMinDays(String(s.checkin_min_days || 30));
                setMaxDays(String(s.checkin_max_days || 45));
                setCheckinPreview(null);
                setSettingsOpen(true);
              }}
              data-testid="discussions-settings-btn"
              className="border-border text-muted-foreground hover:text-foreground rounded-sm text-[10px] h-7 px-2">
              <Settings2 className="w-3 h-3" />
            </Button>
          </span>
        )}>
        <div className="p-4">
          <p className="text-xs text-muted-foreground mb-3">
            Eingehende Broker-Mails (Kampagnen-Antworten, CRM-Broker, manuell geflaggt) und fällige Check-ins für
            🔥 heiße Broker — mit KI-vorbereiteter Antwort im selben E-Mail-Thread.
            {(data?.done_30d || 0) > 0 && <span className="text-emerald-300"> · {data.done_30d} erledigt in 30 Tagen</span>}
          </p>
          {data === null ? (
            <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          ) : data.load_error ? (
            <p className="text-xs text-amber-400 text-center py-4" data-testid="discussions-load-error">
              Discussions konnten nicht geladen werden — wird automatisch erneut versucht.
            </p>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground italic text-center py-4" data-testid="discussions-empty">
              Keine offenen Discussions — neue Broker-Antworten und fällige Check-ins erscheinen hier automatisch.
            </p>
          ) : (
            <div className="space-y-1.5">
              {items.map((d, i) => (
                <div key={d.id} className="border border-border/60 bg-secondary/20 rounded-sm px-3 py-2.5 flex items-start gap-3 flex-wrap"
                  data-testid={`discussion-row-${i}`}>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-foreground font-medium">{d.contact_name}</span>
                      <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 uppercase ${SOURCE_STYLES[d.source] || SOURCE_STYLES.manual}`}
                        data-testid={`discussion-source-${i}`}>{d.source_label}</span>
                      {d.campaign_name && <span className="text-[10px] text-muted-foreground truncate max-w-[180px]">{`„${d.campaign_name}“`}</span>}
                      <span className="text-[10px] text-muted-foreground">{fmtDt(d.received_at)}</span>
                    </div>
                    {d.subject && d.source !== 'checkin' && (
                      <p className="text-[11px] text-foreground/80 truncate mt-0.5">{d.subject}</p>
                    )}
                    {d.body_preview && (
                      <p className="text-[10px] text-muted-foreground/80 mt-0.5 line-clamp-2">{d.body_preview}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                    <button onClick={() => toggleHot(d)} disabled={busy === `hot-${d.id}`}
                      title={d.hot ? 'Heißer Broker — Klick entfernt die Markierung' : 'Als heißen Broker markieren (regelmäßige Check-ins)'}
                      data-testid={`discussion-hot-toggle-${i}`}
                      className={`p-1.5 rounded-sm border transition-colors ${d.hot
                        ? 'border-orange-500/50 bg-orange-500/15 text-orange-400'
                        : 'border-border text-muted-foreground/50 hover:text-orange-400 hover:border-orange-500/40'}`}>
                      <Flame className={`w-3.5 h-3.5 ${d.hot ? 'fill-orange-500/60' : ''}`} />
                    </button>
                    <button onClick={() => openManualInvite(d)} disabled={busy === `invite-${d.id}`}
                      title="Termin-Einladung senden (z.B. telefonisch vereinbart) — ohne KI-Mail"
                      data-testid={`discussion-invite-btn-${i}`}
                      className="p-1.5 rounded-sm border border-border text-muted-foreground/60 hover:text-sky-400 hover:border-sky-500/40 transition-colors">
                      {busy === `invite-${d.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}
                    </button>
                    <Button size="sm" onClick={() => openReply(d)} data-testid={`discussion-reply-btn-${i}`}
                      className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-[10px] h-7 px-2.5">
                      <Sparkles className="w-3 h-3 mr-1" /> {d.source === 'checkin' ? 'KI-Check-in' : 'KI-Antwort'}
                    </Button>
                    <button onClick={() => dismiss(d)} disabled={busy === `dismiss-${d.id}`}
                      title="Ohne Antwort als erledigt markieren" data-testid={`discussion-dismiss-btn-${i}`}
                      className="p-1.5 text-muted-foreground/60 hover:text-red-400 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(data?.done_recent?.length || 0) > 0 && (
            <div className="mt-3">
              <button onClick={() => setShowDone(s => !s)} data-testid="discussions-done-toggle"
                className="text-[10px] font-mono text-muted-foreground hover:text-foreground transition-colors">
                {showDone ? '▾' : '▸'} Erledigt in 30 Tagen ({data.done_recent.length}) — Termin-Einladung weiterhin möglich
              </button>
                {showDone && (
                <div className="space-y-1 mt-2" data-testid="discussions-done-list">
                  {data.done_recent.map((d, i) => (
                    <div key={d.id} className="border border-border/40 bg-secondary/10 rounded-sm"
                      data-testid={`done-discussion-row-${i}`}>
                      <div className="flex items-center gap-2 px-3 py-1.5 flex-wrap">
                        <span className="text-[11px] text-foreground/70">{d.contact_name}</span>
                        {d.auto_sent && (
                          <span className="text-[9px] font-mono border border-violet-500/40 bg-violet-500/10 text-violet-300 rounded px-1 py-0.5"
                            data-testid={`done-auto-badge-${i}`}>AUTO</span>
                        )}
                        {d.subject && <span className="text-[10px] text-muted-foreground truncate max-w-[240px]">{d.subject}</span>}
                        <span className="text-[10px] text-muted-foreground/70 ml-auto">{fmtDt(d.done_at)}</span>
                        {d.invite_sent_at && (
                          <span className="text-[9px] font-mono border border-sky-500/40 bg-sky-500/10 text-sky-300 rounded px-1 py-0.5"
                            data-testid={`done-invite-sent-${i}`}>Einladung gesendet</span>
                        )}
                        {d.sent_body && (
                          <button onClick={() => setExpandedDone(x => x === d.id ? null : d.id)}
                            title="Gesendeten Text ansehen" data-testid={`done-view-btn-${i}`}
                            className="p-1 rounded-sm border border-border text-muted-foreground/60 hover:text-violet-300 hover:border-violet-500/40 transition-colors">
                            <MessagesSquare className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => openManualInvite(d)} disabled={busy === `invite-${d.id}`}
                          title="Termin-Einladung senden (z.B. telefonisch vereinbart)"
                          data-testid={`done-invite-btn-${i}`}
                          className="p-1 rounded-sm border border-border text-muted-foreground/60 hover:text-sky-400 hover:border-sky-500/40 transition-colors">
                          {busy === `invite-${d.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                      {expandedDone === d.id && d.sent_body && (
                        <div className="border-t border-border/40 px-3 py-2" data-testid={`done-sent-body-${i}`}>
                          <p className="text-[10px] text-muted-foreground mb-1">
                            Gesendet {d.auto_sent ? 'automatisch (Auto-Check-in)' : ''} am {fmtDt(d.done_at)}:
                          </p>
                          <p className="text-[11px] text-foreground/80 whitespace-pre-wrap leading-relaxed">{d.sent_body}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CollapsibleSection>

      <Dialog open={!!replyFor} onOpenChange={(o) => !o && !sending && setReplyFor(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[88vh] overflow-y-auto"
          data-testid="discussion-reply-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              {replyFor?.source === 'checkin' ? 'Check-in an' : 'Antwort an'} {replyFor?.contact_name}
            </DialogTitle>
          </DialogHeader>
          {replyFor?.source !== 'checkin' && replyFor?.body_preview && (
            <div className="border border-border/60 bg-secondary/30 rounded-sm p-2.5 max-h-36 overflow-y-auto">
              <p className="text-[10px] text-muted-foreground font-mono uppercase mb-1">Eingegangene E-Mail · {replyFor?.subject || '(kein Betreff)'}</p>
              <p className="text-[11px] text-foreground/80 whitespace-pre-wrap">{replyFor?.body_preview}</p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-1.5" data-testid="tone-salutation-group">
              <span className="text-[10px] text-muted-foreground font-mono uppercase">Anrede</span>
              {[['sehr_geehrt', 'Sehr geehrte/r'], ['guten_tag', 'Guten Tag']].map(([v, l]) => (
                <button key={v} onClick={() => setToneOpt('salutation', v)} disabled={drafting || sending}
                  data-testid={`tone-salutation-${v}`}
                  className={`text-[10px] px-2 py-1 rounded-sm border transition-colors ${tone.salutation === v
                    ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300'
                    : 'border-border text-muted-foreground hover:text-foreground'}`}>{l}</button>
              ))}
            </div>
            <div className="flex items-center gap-1.5" data-testid="tone-form-group">
              <span className="text-[10px] text-muted-foreground font-mono uppercase">Ansprache</span>
              {[['sie', 'Sie'], ['du', 'Du']].map(([v, l]) => (
                <button key={v} onClick={() => setToneOpt('form', v)} disabled={drafting || sending}
                  data-testid={`tone-form-${v}`}
                  className={`text-[10px] px-2 py-1 rounded-sm border transition-colors ${tone.form === v
                    ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300'
                    : 'border-border text-muted-foreground hover:text-foreground'}`}>{l}</button>
              ))}
            </div>
          </div>
          {drafting ? (
            <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> KI bereitet {replyFor?.source === 'checkin' ? 'den Check-in' : 'die Antwort'} vor…
            </div>
          ) : (
            <>
              {(replyFor?.source === 'checkin' || !replyFor?.message_id) && (
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Betreff</label>
                  <Input value={draft.subject} onChange={e => setDraft(v => ({ ...v, subject: e.target.value }))}
                    data-testid="discussion-draft-subject" className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
                </div>
              )}
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  {replyFor?.source === 'checkin' ? 'Check-in-Text' : 'Antwort (wird im selben E-Mail-Thread gesendet)'}
                  — OLTA-Signatur wird automatisch angehängt
                </label>
                <Textarea value={draft.body} onChange={e => setDraft(v => ({ ...v, body: e.target.value }))}
                  data-testid="discussion-draft-body" rows={11}
                  className="bg-secondary border-border text-foreground rounded-sm text-sm leading-relaxed" />
              </div>
            </>
          )}
          <div className="flex items-center justify-between border border-border/60 bg-secondary/30 rounded-sm px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <CalendarPlus className={`w-4 h-4 shrink-0 ${forceInvite ? 'text-sky-400' : 'text-muted-foreground/50'}`} />
              <div>
                <p className="text-xs text-foreground">Termin-Einladung nach dem Senden</p>
                <p className="text-[10px] text-muted-foreground">
                  {forceInvite
                    ? 'Nach dem Senden öffnet sich sicher das Einladungs-Popup (Teams/Kalender) — unabhängig von der KI-Erkennung'
                    : 'Aus = Popup erscheint nur, wenn die KI einen konkreten Termin in deiner Antwort erkennt'}
                </p>
              </div>
            </div>
            <Switch checked={forceInvite} onCheckedChange={setForceInvite} disabled={sending}
              data-testid="force-invite-toggle" />
          </div>
          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => fetchDraft(replyFor, true)} disabled={drafting || sending}
              data-testid="discussion-regenerate-btn"
              className="border-border text-muted-foreground rounded-sm text-xs h-8">
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${drafting ? 'animate-spin' : ''}`} /> Neu generieren
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setReplyFor(null)} disabled={sending}
                className="border-border text-muted-foreground rounded-sm text-xs h-8">Abbrechen</Button>
              <Button onClick={sendReply} disabled={sending || drafting || !draft.body.trim()} data-testid="discussion-send-btn"
                className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-xs h-8 px-4">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Senden</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!invite} onOpenChange={(o) => !o && !inviteSending && setInvite(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[88vh] overflow-y-auto"
          data-testid="invite-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-sky-400" /> Termin-Einladung senden?
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            {invite?.manual ? (
              <>Sende eine Outlook/Teams-Kalender-Einladung an <span className="text-foreground">{invite?.contact_name}</span> —
                z.B. für einen telefonisch vereinbarten Termin. Der Broker erhält sie automatisch per E-Mail.</>
            ) : (
              <>Deine Antwort schlägt einen konkreten Termin vor — soll die Kalender-Einladung
                an <span className="text-foreground">{invite?.contact_name}</span> gesendet werden?
                Der Broker erhält sie automatisch per E-Mail.</>
            )}
          </p>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Betreff</label>
            <Input value={invite?.subject || ''} onChange={e => setInvite(v => ({ ...v, subject: e.target.value }))}
              data-testid="invite-subject" className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Datum</label>
              <Input type="date" value={invite?.date || ''} onChange={e => setInvite(v => ({ ...v, date: e.target.value }))}
                data-testid="invite-date" className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Uhrzeit</label>
              <Input type="time" value={invite?.time || ''} onChange={e => setInvite(v => ({ ...v, time: e.target.value }))}
                data-testid="invite-time" className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Dauer (Min)</label>
              <Input type="number" min="15" max="240" step="15" value={invite?.duration_min || 30}
                onChange={e => setInvite(v => ({ ...v, duration_min: e.target.value }))}
                data-testid="invite-duration" className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
            </div>
          </div>
          <div className="flex items-center justify-between border border-border/60 bg-secondary/30 rounded-sm px-3 py-2.5">
            <div className="flex items-center gap-2.5">
              <Video className={`w-4 h-4 ${invite?.teams ? 'text-sky-400' : 'text-muted-foreground/50'}`} />
              <div>
                <p className="text-xs text-foreground">Microsoft-Teams-Meeting</p>
                <p className="text-[10px] text-muted-foreground">
                  {invite?.teams ? 'Teams-Link wird automatisch in die Einladung eingefügt' : 'Aus = reiner Telefontermin ohne Teams-Link'}
                </p>
              </div>
            </div>
            <Switch checked={!!invite?.teams} onCheckedChange={(v) => setInvite(x => ({ ...x, teams: v }))}
              data-testid="invite-teams-toggle" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Teilnehmer (E-Mail)</label>
            <Input value={invite?.attendee_email || ''} onChange={e => setInvite(v => ({ ...v, attendee_email: e.target.value }))}
              data-testid="invite-attendee" className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Beschreibung</label>
            <Textarea value={invite?.description || ''} onChange={e => setInvite(v => ({ ...v, description: e.target.value }))}
              data-testid="invite-description" rows={2}
              className="bg-secondary border-border text-foreground rounded-sm text-sm" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setInvite(null)} disabled={inviteSending}
              data-testid="invite-skip-btn"
              className="border-border text-muted-foreground rounded-sm text-xs h-8">Überspringen</Button>
            <Button onClick={sendInvite} disabled={inviteSending || !invite?.subject?.trim() || !invite?.date || !invite?.time}
              data-testid="invite-send-btn"
              className="bg-sky-700 hover:bg-sky-600 text-white rounded-sm text-xs h-8 px-4">
              {inviteSending ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><CalendarPlus className="w-3.5 h-3.5 mr-1.5" /> Einladung senden</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[88vh] overflow-y-auto" data-testid="discussions-settings-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2">
              <Flame className="w-5 h-5 text-orange-400" /> Check-in-Einstellungen
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2.5">
            <div>
              <p className="text-xs text-foreground font-medium">Check-ins automatisch senden</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                KI schreibt und versendet das Follow-up selbst (werktags 9–17 Uhr), über das Postfach der
                ursprünglichen Kampagne. Erfasst 🔥-Broker UND Broker, die die Sequenz komplett ohne Antwort
                durchlaufen haben. Bei Fehlern bleibt der Entwurf hier liegen + Push-Nachricht.
              </p>
            </div>
            <Switch checked={autosend} onCheckedChange={setAutosend} data-testid="checkin-autosend-toggle" />
          </div>
          {autosend ? (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground block mb-1">Frühestens nach (Tagen)</label>
                <Input type="number" min="7" max="120" value={minDays} onChange={e => setMinDays(e.target.value)}
                  data-testid="checkin-min-days-input"
                  className="bg-secondary border-border text-foreground rounded-sm h-9" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground block mb-1">Spätestens nach (Tagen)</label>
                <Input type="number" min="7" max="180" value={maxDays} onChange={e => setMaxDays(e.target.value)}
                  data-testid="checkin-max-days-input"
                  className="bg-secondary border-border text-foreground rounded-sm h-9" />
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Wochen ohne Kontakt (1–12)</label>
              <Input type="number" min="1" max="12" value={weeks} onChange={e => setWeeks(e.target.value)}
                data-testid="discussions-checkin-weeks-input"
                className="bg-secondary border-border text-foreground rounded-sm h-9" />
              <p className="text-[10px] text-muted-foreground mt-1">
                Heiße Broker (🔥) und Broker mit komplett durchlaufener Sequenz ohne Antwort bekommen nach dieser
                Zeit einen Check-in-Eintrag mit KI-Entwurf in dieser Liste — Versand per Klick durch dich.
              </p>
            </div>
          )}
          {autosend && (
            <p className="text-[10px] text-muted-foreground">
              Jeder 🔥-Broker bekommt sein eigenes zufälliges Intervall in dieser Spanne — wirkt natürlicher als ein
              fester Rhythmus. Gesendete Check-ins erscheinen unter „Erledigt" mit einsehbarem Text.
            </p>
          )}
          <div className="border border-border rounded-sm">
            <button onClick={loadCheckinPreview} disabled={previewLoading} data-testid="checkin-preview-btn"
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">
              {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-violet-400" />}
              Vorschau: So sieht ein Check-in aus (KI, wird nicht gesendet)
            </button>
            {checkinPreview && (
              <div className="border-t border-border px-3 py-2.5 space-y-1.5" data-testid="checkin-preview-box">
                <p className="text-[10px] text-muted-foreground">
                  Beispiel für <span className="text-foreground">{checkinPreview.broker}</span>
                  {checkinPreview.company ? ` (${checkinPreview.company})` : ''}:
                </p>
                <p className="text-[11px] text-foreground font-medium">Betreff: {checkinPreview.subject || '—'}</p>
                <p className="text-[11px] text-foreground/80 whitespace-pre-wrap leading-relaxed">{checkinPreview.body}</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSettingsOpen(false)} disabled={weeksSaving}
              className="border-border text-muted-foreground rounded-sm text-xs h-8">Abbrechen</Button>
            <Button onClick={saveWeeks} disabled={weeksSaving} data-testid="discussions-settings-save"
              className="bg-orange-600 hover:bg-orange-500 text-white rounded-sm text-xs h-8 px-4">
              {weeksSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AddMailDialog open={addOpen} mode="discussion" onClose={() => setAddOpen(false)}
        onDone={() => { setAddOpen(false); load(); }} />
    </>
  );
}
